import React, { useEffect, useState } from "react";
import { Dna, RefreshCw, BookOpenText, Activity, Zap, Moon, GitMerge, Telescope } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import OctonDNA from "@/components/OctonDNA";
import Regeneration from "@/components/Regeneration";

const ACTION_COLORS = {
  compress: "text-cyan-300 bg-cyan-400/10 border-cyan-400/25",
  decay: "text-slate-400 bg-white/5 border-white/10",
  reassess: "text-amber-300 bg-amber-400/10 border-amber-400/25",
  prediction_error: "text-rose-300 bg-rose-400/10 border-rose-400/25",
};

function Bold({ text }) {
  const parts = text.split("**");
  return parts.map((p, i) => (i % 2 ? <strong key={i} className="text-cyan-200 font-semibold">{p}</strong> : <span key={i}>{p}</span>));
}

// Minimal renderer for the charter's markdown (headings, tables, code, bold).
function CharterView({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      blocks.push(<h3 key={i} className="font-display font-semibold text-cyan-300 text-sm mt-6 mb-2">{line.slice(3)}</h3>);
    } else if (line.startsWith("# ")) {
      blocks.push(<h2 key={i} className="font-display font-bold text-white text-lg mb-2">{line.slice(2)}</h2>);
    } else if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        if (!/^\|[\s\-|]+\|$/.test(lines[i])) rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i += 1;
      }
      i -= 1;
      blocks.push(
        <table key={i} className="my-3 w-full text-[11px]">
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className={ri === 0 ? "text-cyan-300 font-mono-data uppercase text-[9px]" : "text-slate-300 border-t border-white/5"}>
                {r.map((c, ci) => <td key={ci} className="py-1.5 pr-4 align-top">{c.replace(/`/g, "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (line.startsWith("    ")) {
      const code = [];
      while (i < lines.length && (lines[i].startsWith("    ") || lines[i].trim() === "")) {
        code.push(lines[i].slice(4));
        i += 1;
        if (i < lines.length && lines[i].trim() === "" && !(lines[i + 1] || "").startsWith("    ")) break;
      }
      i -= 1;
      blocks.push(<pre key={i} className="my-3 p-3 rounded-lg bg-black/50 border border-cyan-400/10 text-[10px] text-cyan-200/90 overflow-x-auto whitespace-pre">{code.join("\n").trimEnd()}</pre>);
    } else if (line.trim() === "---") {
      blocks.push(<hr key={i} className="my-4 border-white/5" />);
    } else if (line.trim()) {
      const para = [line];
      while (i + 1 < lines.length && lines[i + 1].trim() && !/^(#|\||    |---)/.test(lines[i + 1])) {
        i += 1;
        para.push(lines[i]);
      }
      blocks.push(<p key={i} className="text-[12px] text-slate-300 leading-relaxed my-2"><Bold text={para.join(" ").replace(/`/g, "")} /></p>);
    }
    i += 1;
  }
  return <div data-testid="charter-content">{blocks}</div>;
}

function StatChip({ label, value, accent = "cyan" }) {
  return (
    <div className={`rounded-lg border border-${accent}-400/15 bg-${accent}-400/[0.04] px-3 py-2`}>
      <p className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-lg font-display font-bold text-white">{value}</p>
    </div>
  );
}

const VERDICT_COLORS = {
  CORROBORATES: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  CONTRADICTS: "text-rose-300 bg-rose-400/10 border-rose-400/30",
  EXTENDS: "text-cyan-300 bg-cyan-400/10 border-cyan-400/30",
  INCONCLUSIVE: "text-slate-400 bg-white/5 border-white/15",
  LANDMARK: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  NOTABLE: "text-purple-300 bg-purple-400/10 border-purple-400/30",
  ROUTINE: "text-slate-400 bg-white/5 border-white/15",
};

const KIND_LABELS = { weak_belief: "weak belief", surprise: "surprise", intent_gap: "unanswered gap", frontier: "frontier watch" };

function CuriosityFeed({ feed }) {
  if (!feed) return <p className="text-xs text-slate-500 animate-pulse">Loading curiosity dives...</p>;
  return (
    <div className="space-y-4" data-testid="curiosity-feed">
      <p className="text-[11px] text-slate-500">
        {feed.total_dives} autonomous research dive{feed.total_dives === 1 ? "" : "s"} so far
        {feed.last_cycle ? ` · last cycle ${(feed.last_cycle.ran_at || "").replace("T", " ").slice(0, 16)} UTC (${feed.last_cycle.dives} dives)` : ""}.
        The brain picks its own weakest beliefs, surprises and unanswered gaps, then dives into the literature - unprompted.
      </p>
      {!feed.dives.length && <p className="text-[11px] text-slate-500" data-testid="curiosity-empty">No dives yet - press Dive Now or wait for tonight's sleep cycle.</p>}
      {feed.dives.map((d, i) => (
        <div key={d.id} data-testid={`curiosity-dive-${i}`} className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`px-2 py-0.5 rounded border text-[9px] font-mono-data uppercase tracking-wider ${VERDICT_COLORS[d.verdict] || VERDICT_COLORS.INCONCLUSIVE}`}>{d.verdict}</span>
            <span className="px-2 py-0.5 rounded border border-purple-400/25 bg-purple-400/10 text-purple-300 text-[9px] font-mono-data uppercase tracking-wider">{KIND_LABELS[d.kind] || d.kind}</span>
            <span className="text-[9px] font-mono-data text-slate-600">{(d.occurred_at || "").replace("T", " ").slice(0, 16)} UTC</span>
          </div>
          <p className="text-[12px] text-slate-200 leading-snug mb-1.5">{d.topic}</p>
          <p className="text-[10px] font-mono-data text-cyan-300/70 mb-2">query: {d.query}</p>
          <p className="text-[11px] text-slate-400 leading-relaxed whitespace-pre-wrap">{d.memo}</p>
          {!!d.sources.length && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {d.sources.map((s, si) => (
                <span key={si} className={`px-2 py-0.5 rounded-full border text-[8px] font-mono-data ${s.type === "journal" ? "border-emerald-400/20 text-emerald-300/80" : "border-cyan-400/20 text-cyan-300/70"}`}>
                  {s.type} · {s.ref}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CompartmentLeaderboard({ board }) {
  if (!board) return null;
  const max = Math.max(...board.leaderboard.map((r) => r.score), 1);
  return (
    <section data-testid="compartment-leaderboard">
      <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">
        Fleet Teaching Leaderboard - last {board.days} days · which ships taught the brain the most
      </h3>
      {!board.leaderboard.length && <p className="text-[11px] text-slate-500">No ship taught the brain anything this week - hive pushes, filed records and concept firings will rank here.</p>}
      <div className="space-y-1.5">
        {board.leaderboard.map((r, i) => (
          <div key={r.project} className="flex items-center gap-2" data-testid={`leaderboard-row-${i}`}>
            <span className="w-5 text-[10px] font-mono-data text-slate-500 text-right">{i + 1}</span>
            <span className="w-40 truncate text-[11px] text-slate-200">{r.project}</span>
            <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full rounded-full ${i === 0 ? "bg-amber-400/80" : i < 3 ? "bg-cyan-400/70" : "bg-cyan-400/40"}`} style={{ width: `${Math.max(4, (r.score / max) * 100)}%` }} />
            </div>
            <span className="w-8 text-[10px] font-mono-data text-cyan-300 text-right">{r.score}</span>
            <span className="hidden sm:block w-60 text-[8px] font-mono-data text-slate-500 truncate">
              {r.concepts_fired} concepts · {r.memories} mem · {r.records} rec · {r.insights_cited} insights{r.insights_new ? ` (+${r.insights_new} new)` : ""}
            </span>
          </div>
        ))}
      </div>
      {!!board.quiet.length && (
        <p className="text-[9px] font-mono-data text-slate-600 mt-2">quiet this week: {board.quiet.join(" · ")}</p>
      )}
    </section>
  );
}

function ConfidenceBars({ dist }) {
  const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
  const colors = { "<0.5": "bg-rose-400/70", "0.5-0.7": "bg-amber-400/70", "0.7-0.9": "bg-cyan-400/70", ">=0.9": "bg-emerald-400/70" };
  return (
    <div className="space-y-1.5" data-testid="confidence-bars">
      {Object.entries(dist || {}).map(([bucket, n]) => (
        <div key={bucket} className="flex items-center gap-2">
          <span className="w-14 text-[9px] font-mono-data text-slate-500">{bucket}</span>
          <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div className={`h-full rounded-full ${colors[bucket]}`} style={{ width: `${Math.max(n ? 6 : 0, (n / total) * 100)}%` }} />
          </div>
          <span className="w-6 text-[10px] font-mono-data text-slate-400 text-right">{n}</span>
        </div>
      ))}
    </div>
  );
}

export default function GenomeCore() {
  const [data, setData] = useState(null);
  const [mods, setMods] = useState(null);
  const [trends, setTrends] = useState([]);
  const [curiosity, setCuriosity] = useState(null);
  const [board, setBoard] = useState(null);
  const [tab, setTab] = useState("genome");
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/brain/genome").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the genome."));
    api.get("/brain/self-modifications", { params: { limit: 60 } }).then((r) => setMods(r.data)).catch(() => {});
    api.get("/brain/genome-trends").then((r) => setTrends(r.data)).catch(() => {});
    api.get("/brain/curiosity-feed").then((r) => setCuriosity(r.data)).catch(() => {});
    api.get("/brain/compartment-leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runAction = async (key, path, label) => {
    setBusy(key);
    try {
      const res = await api.post(path);
      toast.success(res.data?.note || `${label} complete.`);
      setTimeout(load, key === "replay" ? 1500 : 8000);
    } catch {
      toast.error(`${label} failed.`);
    } finally {
      setBusy(null);
    }
  };

  const g = data?.genome;
  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="genome-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2">
            <Dna className="w-5 h-5 text-cyan-300" /> OCTON Core
          </h2>
          <p className="text-[11px] text-slate-500 mt-1">
            {g ? `${g.architecture?.name} · genome v${g.version} · charter ${g.charter_hash}` : "Reading the genome..."}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button data-testid="genome-subtab-genome" onClick={() => setTab("genome")}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${tab === "genome" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>
            <Activity className="w-3 h-3 inline mr-1" />Genome
          </button>
          <button data-testid="genome-subtab-charter" onClick={() => setTab("charter")}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${tab === "charter" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>
            <BookOpenText className="w-3 h-3 inline mr-1" />Charter
          </button>
          <button data-testid="genome-subtab-curiosity" onClick={() => setTab("curiosity")}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${tab === "curiosity" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>
            <Telescope className="w-3 h-3 inline mr-1" />Curiosity
          </button>
          <button data-testid="run-curiosity-button" disabled={busy} onClick={() => runAction("curiosity", "/brain/curiosity", "Curiosity dive")}
            className="px-3 py-1.5 rounded-lg bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/25 text-amber-300 text-[10px] font-mono-data uppercase tracking-wider disabled:opacity-50">
            <Telescope className="w-3 h-3 inline mr-1" />{busy === "curiosity" ? "Diving..." : "Dive Now"}
          </button>
          <button data-testid="run-replay-button" disabled={busy} onClick={() => runAction("replay", "/brain/replay", "Hippocampal replay")}
            className="px-3 py-1.5 rounded-lg bg-rose-400/10 hover:bg-rose-400/20 border border-rose-400/25 text-rose-300 text-[10px] font-mono-data uppercase tracking-wider disabled:opacity-50">
            <Zap className="w-3 h-3 inline mr-1" />{busy === "replay" ? "Replaying..." : "Replay"}
          </button>
          <button data-testid="run-metabolize-button" disabled={busy} onClick={() => runAction("metab", "/brain/metabolize", "Memory metabolism")}
            className="px-3 py-1.5 rounded-lg bg-purple-400/10 hover:bg-purple-400/20 border border-purple-400/25 text-purple-300 text-[10px] font-mono-data uppercase tracking-wider disabled:opacity-50">
            <Moon className="w-3 h-3 inline mr-1" />{busy === "metab" ? "Metabolising..." : "Metabolise"}
          </button>
          <button data-testid="run-fleet-consolidate-button" disabled={busy} onClick={() => runAction("fleet", "/brain/fleet-consolidate", "Fleet consolidation")}
            className="px-3 py-1.5 rounded-lg bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/25 text-emerald-300 text-[10px] font-mono-data uppercase tracking-wider disabled:opacity-50">
            <GitMerge className="w-3 h-3 inline mr-1" />{busy === "fleet" ? "Consolidating..." : "Consolidate"}
          </button>
          <button data-testid="genome-refresh-button" onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!data && <p className="text-xs text-slate-500 animate-pulse">Sequencing...</p>}

      {data && tab === "charter" && <CharterView text={data.charter} />}

      {tab === "curiosity" && <CuriosityFeed feed={curiosity} />}

      {g && tab === "genome" && (
        <div className="space-y-6">
          <OctonDNA />
          <Regeneration />
          <section data-testid="arch-map">
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">Cognitive Subsystems - the LLM is one subsystem, not the brain</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {Object.entries(g.architecture?.subsystems || {}).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-white/8 bg-white/[0.02] p-2.5">
                  <p className="text-[9px] font-mono-data uppercase tracking-wider text-cyan-300">{k.replace(/_/g, " ")}</p>
                  <p className="text-[10px] text-slate-400 mt-1 leading-snug">{v}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">Memory Metabolism State</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatChip label="Hippocampal episodes" value={g.memory_state.episodic} />
              <StatChip label="Ephemeral (decaying)" value={g.memory_state.ephemeral} />
              <StatChip label="Compressed semantic" value={g.memory_state.compressed_semantic} />
              <StatChip label="Filed records" value={g.memory_state.records} />
              <StatChip label="Concepts" value={g.memory_state.concepts_total} />
              <StatChip label="Synapses" value={g.memory_state.synapses_total} />
              <StatChip label="People known" value={g.memory_state.people_known} />
              <StatChip label="Visual prototypes" value={g.memory_state.visual_prototypes} />
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <section>
              <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">
                Bayesian Belief Confidence - {g.fleet_knowledge.active_insights} active insights{g.fleet_knowledge.mean_confidence != null ? ` · mean ${g.fleet_knowledge.mean_confidence}` : ""}
              </h3>
              <ConfidenceBars dist={g.fleet_knowledge.confidence_distribution} />
            </section>
            <section data-testid="prediction-error-stats">
              <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">Prediction Error - last replay</h3>
              {g.prediction_error ? (
                <div className="grid grid-cols-2 gap-2">
                  <StatChip label="Episodes replayed" value={g.prediction_error.replayed} />
                  <StatChip label="High-error (surprises)" value={g.prediction_error.high_error} accent="rose" />
                  <StatChip label="Mean error" value={g.prediction_error.mean_prediction_error ?? "-"} />
                  <StatChip label="Beliefs reassessed" value={g.prediction_error.beliefs_reassessed} accent="amber" />
                </div>
              ) : <p className="text-[11px] text-slate-500">No replay has run yet - trigger one above or wait for tonight's sleep cycle.</p>}
            </section>
          </div>

          <section data-testid="genome-trends">
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">
              Genome Growth - cycle by cycle ({trends.length} snapshot{trends.length === 1 ? "" : "s"})
            </h3>
            {trends.length >= 2 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="h-44 rounded-lg border border-white/8 bg-white/[0.02] p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <XAxis dataKey="built_at" tickFormatter={(v) => (v || "").slice(5, 10)} tick={{ fontSize: 9, fill: "#64748b" }} stroke="#1e293b" />
                      <YAxis tick={{ fontSize: 9, fill: "#64748b" }} stroke="#1e293b" />
                      <Tooltip contentStyle={{ background: "#050a0f", border: "1px solid rgba(0,240,255,.25)", borderRadius: 8, fontSize: 10 }} labelFormatter={(v) => (v || "").replace("T", " ").slice(0, 16)} />
                      <Legend wrapperStyle={{ fontSize: 9 }} />
                      <Line type="monotone" dataKey="concepts" stroke="#67e8f9" strokeWidth={1.5} dot={false} name="Concepts" />
                      <Line type="monotone" dataKey="synapses" stroke="#c084fc" strokeWidth={1.5} dot={false} name="Synapses" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-44 rounded-lg border border-white/8 bg-white/[0.02] p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <XAxis dataKey="built_at" tickFormatter={(v) => (v || "").slice(5, 10)} tick={{ fontSize: 9, fill: "#64748b" }} stroke="#1e293b" />
                      <YAxis yAxisId="l" tick={{ fontSize: 9, fill: "#64748b" }} stroke="#1e293b" />
                      <YAxis yAxisId="r" orientation="right" domain={[0, 1]} tick={{ fontSize: 9, fill: "#64748b" }} stroke="#1e293b" />
                      <Tooltip contentStyle={{ background: "#050a0f", border: "1px solid rgba(0,240,255,.25)", borderRadius: 8, fontSize: 10 }} labelFormatter={(v) => (v || "").replace("T", " ").slice(0, 16)} />
                      <Legend wrapperStyle={{ fontSize: 9 }} />
                      <Line yAxisId="l" type="monotone" dataKey="active_insights" stroke="#34d399" strokeWidth={1.5} dot={false} name="Active insights" />
                      <Line yAxisId="l" type="monotone" dataKey="compressed" stroke="#fbbf24" strokeWidth={1.5} dot={false} name="Compressed units" />
                      <Line yAxisId="r" type="monotone" dataKey="mean_confidence" stroke="#f472b6" strokeWidth={1.5} dot={false} name="Mean confidence" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-slate-500">The growth chart appears once two or more nightly snapshots exist - each sleep cycle adds one.</p>
            )}
          </section>

          <CompartmentLeaderboard board={board} />

          <section>
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">Strongest Synapses</h3>
            <div className="space-y-1">
              {(g.relationships || []).slice(0, 8).map((e, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-56 truncate text-[10px] text-slate-300">{e.source} ↔ {e.target}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-cyan-400/60" style={{ width: `${Math.round(e.strength * 100)}%` }} />
                  </div>
                  <span className="w-10 text-[9px] font-mono-data text-slate-500 text-right">{e.strength}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">Value Weights (salience gate)</h3>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(g.value_weights || {}).map(([k, v]) => (
                <span key={k} className="px-2 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[9px] font-mono-data text-slate-400">{k}: <span className="text-cyan-300">{v}</span></span>
              ))}
            </div>
          </section>

          <section data-testid="selfmod-timeline">
            <h3 className="text-[10px] font-mono-data uppercase tracking-widest text-cyan-300/80 mb-2">
              Self-Modification History - {mods?.total ?? g.self_modification_history.total} structural changes (append-only)
            </h3>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {(mods?.modifications || g.self_modification_history.recent || []).map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`mt-0.5 px-1.5 py-0.5 rounded border text-[8px] font-mono-data uppercase tracking-wider whitespace-nowrap ${ACTION_COLORS[m.action] || "text-slate-400 bg-white/5 border-white/10"}`}>{m.action}</span>
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-400 truncate">{JSON.stringify(m.details)}</p>
                    <p className="text-[8px] font-mono-data text-slate-600">{(m.ts || "").replace("T", " ").slice(0, 19)} UTC</p>
                  </div>
                </div>
              ))}
              {!(mods?.modifications || []).length && !(g.self_modification_history.recent || []).length && (
                <p className="text-[11px] text-slate-500">No structural self-changes yet - they appear here as the metabolism and replay run.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
